import {
	getMeQueryKey,
	getWorkspaceQueryKey,
	useMeQuery,
	type UserQueryField,
	useWorkspaceQuery,
	type WorkspaceQueryField,
} from '@cheqroom/api';
import { type QueryClient } from '@tanstack/react-query';

/**
 * The session layer shared by web and mobile: the signed-in user (`users/call/me`) and workspace
 * (`groups/:id`) as two react-query queries. Each app adds what is its own on top — mobile its access
 * token and sign-in actions, web the legacy `users.me` hydration — and its own typed context.
 *
 * The field lists are part of the query keys, so every app passes its own and keeps them stable.
 */

type SessionQueriesOptions<MeF extends readonly UserQueryField[], WsF extends readonly WorkspaceQueryField[]> = {
	/** Defaults to the signed-in user's `group` (so it follows every session the cache holds). */
	workspaceId?: string;
	meFields: MeF;
	workspaceFields: WsF;
	/** Fetch only when true (mobile: once there is an access token). Defaults to true. */
	enabled?: boolean;
	/**
	 * `false` reads the cache only and never fetches. Web: the legacy shell owns the session and
	 * hydrates it ({@link hydrateSessionQueries}); a React refetch would replace that data.
	 */
	fetch?: boolean;
	onWorkspaceError?: (error: unknown) => void;
};

/** A group as the legacy shell holds it: populated, with `_id`. */
export type SessionGroup = { _id?: string } & Record<string, unknown>;

/** What {@link hydrateSessionQueries} takes. */
export type HydrateSessionInput = {
	user?: (object & { group?: SessionGroup | string | null }) | null;
	group?: SessionGroup | null;
	meFields: readonly UserQueryField[];
	workspaceFields: readonly WorkspaceQueryField[];
};

/**
 * The `{ user, workspace }` state each app's session context exposes; apps add their own
 * statuses (mobile: `signedOut`) and actions on top.
 */
export type SessionState<U, W> =
	| { status: 'loading'; user: null; workspace: null }
	| { status: 'signedIn'; user: U; workspace: W | null };

/** Session data is plain API JSON; a JSON copy works on web, jsdom and React Native alike. */
const copyJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** A `group` is an id or the populated group. */
const groupIdOf = (group: unknown): string =>
	typeof group === 'string' ? group : ((group as SessionGroup | null | undefined)?._id ?? '');

export const useSessionQueries = <
	const MeF extends readonly UserQueryField[],
	const WsF extends readonly WorkspaceQueryField[],
>({
	workspaceId,
	meFields,
	workspaceFields,
	enabled = true,
	fetch = true,
	onWorkspaceError,
}: SessionQueriesOptions<MeF, WsF>) => {
	const me = useMeQuery({ fields: meFields }, { enabled: enabled && fetch });
	const resolvedWorkspaceId = workspaceId || groupIdOf((me.data as { group?: unknown } | undefined)?.group);
	const workspace = useWorkspaceQuery(
		{ id: resolvedWorkspaceId, fields: workspaceFields },
		{ enabled: enabled && fetch && !!resolvedWorkspaceId, onError: onWorkspaceError }
	);

	return {
		user: me.data ?? null,
		workspace: workspace.data ?? null,
		isMeLoading: me.isInitialLoading,
		isMeError: me.isError,
		isWorkspaceLoading: workspace.isInitialLoading,
	};
};

/**
 * Fill the session queries from a session loaded elsewhere (web: the legacy `users.me` response),
 * under the same keys `useSessionQueries` reads. `group` alone updates only the workspace;
 * `group: null` updates only the user (the workspace is left as it is).
 *
 * The data is copied: the cache never shares objects with the caller, so later in-place changes on
 * either side do not leak, and a new hydration always notifies. Re-hydrate to publish a change.
 */
export const hydrateSessionQueries = (
	queryClient: QueryClient,
	{ user, group, meFields, workspaceFields }: HydrateSessionInput
) => {
	if (user) {
		queryClient.setQueryData(getMeQueryKey(meFields), copyJson(user));
	}
	// `undefined`: take the workspace from the user; `null`: leave it.
	const workspace = group === undefined ? user?.group : group;
	if (workspace && typeof workspace !== 'string' && workspace._id) {
		const picked: Record<string, unknown> = { _id: workspace._id };
		for (const field of workspaceFields) {
			const key = String(field).split('.')[0];
			if (key in workspace) picked[key] = copyJson(workspace[key]);
		}
		queryClient.setQueryData(getWorkspaceQueryKey({ id: workspace._id, fields: workspaceFields }), picked);
	}
};
